/*
The gift message component manages the cart's attributes values and fetching updates to the cart when the values change.
 */

import Component from "./../../component";

export const CartDrawerGiftMessageConfig = {
    signature: 'CartDrawerGiftMessage',
    baseHtmlClass: 'go-cart__drawer__gift_message',
    selector: '.go-cart__drawer__gift_message',

    initedHtmlClassModifier: 'inited',
    isGiftWrapHtmlClassModifier: 'is_gift_wrap',
    hasMessageHtmlClassModifier: 'has_message',

    headerElementSelector: '.cart__drawer__gift_message__header',
    bodyElementSelector: '.cart__drawer__gift_message__body',
    messageOutputElementSelector: '.cart__drawer__gift_message__faux_item__message',
    messageInputSelector: '[name="attributes[gift-message]"]',
    commitButtonSelector: '.go-cart__drawer__gift_message__commit_button',
    resetButtonSelector: '.go-cart__drawer__gift_message__reset_button',
    isGiftWrapInputSelector: '[name="attributes[is-gift-wrap]"]',

    isGiftWrapKey: 'is-gift-wrap',
    giftMessageKey: 'gift-message'
};

export class CartDrawerGiftMessage extends Component {

    constructor(id_or_element, goCart) {
        super({
            element: id_or_element,
            config: CartDrawerGiftMessageConfig
        });

        this.goCart = goCart;

        this.noteManager = this.goCart?.noteManager;

        this.headerElement = this.element.querySelector(CartDrawerGiftMessageConfig.headerElementSelector);
        this.bodyElement = this.element.querySelector(CartDrawerGiftMessageConfig.bodyElementSelector);

        this.messageOutputElement = this.element.querySelector(CartDrawerGiftMessageConfig.messageOutputElementSelector);

        this.messageInput = this.element.querySelector(CartDrawerGiftMessageConfig.messageInputSelector);
        if (this.messageInput) {
            this.messageInput.addEventListener('keyup', this.handleMessageInputChange.bind(this));
        }

        this.commitButton = this.element.querySelector(CartDrawerGiftMessageConfig.commitButtonSelector);
        if (this.commitButton) {
            this.commitButton.addEventListener('click', this.handleCommitButtonClick.bind(this))
        }

        this.resetButton = this.element.querySelector(CartDrawerGiftMessageConfig.resetButtonSelector);
        if (this.resetButton) {
            this.resetButton.addEventListener('click', this.handleResetButtonClick.bind(this))
        }

        this.isGiftWrapInput = this.element.querySelector(CartDrawerGiftMessageConfig.isGiftWrapInputSelector);

        this.isGiftWrap = null;
        this.message = null;
        this.storedMessage = this.getMessageInputValue(); // The gift message on the server will be in the input.
        this.messageInputRows = (this.messageInput ? this.messageInput.getAttribute('rows') : 1);

        this.configureStateFromInputs();

        this.inited = true;
        this.render();
    }

    render() {
        this.conditionallySetHtmlClassState(this.inited, CartDrawerGiftMessageConfig.initedHtmlClassModifier);
        this.conditionallySetHtmlClassState(this.isGiftWrap, CartDrawerGiftMessageConfig.isGiftWrapHtmlClassModifier);
        this.conditionallySetHtmlClassState(this.hasMessageInputValue(), CartDrawerGiftMessageConfig.hasMessageHtmlClassModifier);

        this.renderMessageOutputElement();
        this.renderCommitButton();
        this.renderMessageInput();
        this.renderIsGiftWrapInput();

        this.renderCartDrawerSummaryComponent();
    }

    renderMessageOutputElement() {
        if (!this.messageOutputElement) return;
        // We render the message stored on the server.
        let outputElement = document.createElement('pre');
        outputElement.appendChild(document.createTextNode(this.hasStoredMessage() ? this.storedMessage : ''));
        this.messageOutputElement.innerHTML = '';
        this.messageOutputElement.appendChild(outputElement);
    }

    renderCommitButton() {
        if (!this.commitButton) return;
        this.commitButton.disabled = !this.hasMessageInputValue();
    }

    renderMessageInput() {
        if (!this.messageInput) return;
        this.messageInput.value = this.message;
        if (this.hasMessageInputValue()) {
            this.messageInput.setAttribute('rows', 5);
        } else {
            this.messageInput.setAttribute('rows', this.messageInputRows);
        }
    }

    renderIsGiftWrapInput() {
        if (!this.isGiftWrapInput) return;
        this.isGiftWrapInput.value = this.isGiftWrap;
    }

    renderCartDrawerSummaryComponent() {
        if (!this.goCart || !this.goCart.cartDrawerSummaryComponent) return;
        this.goCart.cartDrawerSummaryComponent.render();
    }

    handleMessageInputChange() {
        if (this.hasMessageInputValue()) {
            this.message = this.getMessageInputValue();
        } else {
            this.message = '';
        }
        this.render();
    }

    handleCommitButtonClick() {
        this.configureStateFromInputs();
        this.render();
        this.updateCart().then(this.render.bind(this));
    }

    handleResetButtonClick() {
        this.setMessageInputValue('');
        this.configureStateFromInputs();
        this.render();
        this.updateCart().then(this.render.bind(this));
    }

    async updateCart() {
        await window.fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(await this.getSubmitData()),
        })
            .then((response) => response.json())
            .then((cartJson) => this.updateCartHandler(cartJson))
            .catch((error) => {
                throw new Error(error)
            });
    }
    
    updateCartHandler(cartJson) {
        this.storedMessage = cartJson['attributes']['gift-message'];
    }

    configureStateFromInputs() {
        if (this.hasMessageInputValue()) {
            this.isGiftWrap = true;
            this.message = this.getMessageInputValue();
        } else {
            this.isGiftWrap = false;
            this.message = '';
        }
    }

    getMessageInputValue() {
        if (!this.messageInput) return null;
        return this.messageInput.value;
    }

    setMessageInputValue(value) {
        if (!this.messageInput) return null;
        return this.messageInput.value = value;
    }

    hasMessageInputValue() {
        const value = this.getMessageInputValue();
        return (typeof value !== 'undefined') && (value !== null) && (value !== '');
    }

    hasStoredMessage() {
        if (this.storedMessage == null || typeof this.storedMessage === 'undefined') return false;
        return this.storedMessage !== '';
    }

    async getSubmitData() {
        let data = {attributes: {}};
        data['attributes'][CartDrawerGiftMessageConfig.isGiftWrapKey] = this.isGiftWrap;
        data['attributes'][CartDrawerGiftMessageConfig.giftMessageKey] = this.message;
        data['note'] =  await this.getNote();
        return data;
    }

    async getNote() {
        if (typeof this.noteManager === 'undefined' || typeof this.noteManager === 'null') return;
        await this.noteManager.setForKey(CartDrawerGiftMessageConfig.isGiftWrapKey, (this.isGiftWrap ? true : null));
        return this.noteManager.get();
    }

}
